import { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";

import { BSSelect } from "../reactSelectBSWrapper";
import { FormControlWrapper } from "./formControlWrapper";

// Debounce a promise-returning fn: collapses rapid calls (keystrokes) into one
// invocation, resolving every pending caller with the latest result. Keeps the
// async search from firing a request per keystroke.
export const debouncePromise = (fn, waitMs) => {
	let timer;
	let pending = [];

	return (...args) =>
		new Promise((resolve, reject) => {
			clearTimeout(timer);
			pending.push({ resolve, reject });
			timer = setTimeout(() => {
				const waiters = pending;
				pending = [];
				fn(...args).then(
					(result) => waiters.forEach((w) => w.resolve(result)),
					(error) => waiters.forEach((w) => w.reject(error))
				);
			}, waitMs);
		});
};

// Default map from a pre-selected field datum (edit mode) to a react-select
// option. Falls back to the email as the label when no formatter is supplied.
const defaultSelectedOptionFn = (datum) => {
	const email = datum?.email ?? datum;
	return { value: email, label: email, id: email };
};

// Dropdown backed by a server-side search (react-select async). Use instead of
// DROPDOWN / MULTI_DROPDOWN when the option set is too large to load up front
// (e.g. org users). `loadOptions(inputValue)` must resolve to
// [{ id, value, label }] and is expected to be cached by the caller; keystroke
// debouncing is handled here (`debounceMs`).
//
// Single-select (default): `setValue` receives the chosen option's value (or
// undefined when cleared).
//
// Multi-select (`isMulti`): `setValue` receives an array of the selected
// options' values. Already-selected values (edit mode) are seeded from
// `fieldData` (or `selectedRows` when `isSelectedRowValues`) via
// `selectedOptionFn`, so they render even though they aren't in the current
// search page.
export const FieldAsyncDropdown = ({
	isDisabled,
	error,
	placeholder = "",
	title = "",
	autoFocus,
	tooltip,
	loadOptions = async () => [],
	defaultOptions = true,
	debounceMs = 500,
	isMulti = false,
	fieldData,
	isSelectedRowValues = false,
	selectedRows = [],
	selectedOptionFn = defaultSelectedOptionFn,
	setValue = (_value) => {},
}) => {
	const initialSelected = useMemo(() => {
		if (!isMulti) return null;
		const source = isSelectedRowValues ? selectedRows : fieldData;
		if (!Array.isArray(source)) return [];
		return source.map(selectedOptionFn).filter((o) => o != null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [selected, setSelected] = useState(initialSelected);

	// Seed the form value with the pre-selected values (edit mode) on mount, so
	// the submit handler sees the existing selection even if the user changes
	// nothing.
	useEffect(() => {
		if (isMulti)
			setValue((initialSelected ?? []).map((o) => o.value));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const debouncedLoadOptions = useMemo(
		() => debouncePromise(loadOptions, debounceMs),
		[loadOptions, debounceMs]
	);

	const handleChange = (option) => {
		setSelected(option);
		if (isMulti) {
			setValue((option ?? []).map((o) => o.value));
		} else {
			setValue(option ? option.value : undefined);
		}
	};

	return (
		<Form.Group data-testid="test-fieldAsyncDropdown">
			<FormControlWrapper title={title} tooltip={tooltip} error={error}>
				<BSSelect
					async
					cacheOptions
					defaultOptions={defaultOptions}
					loadOptions={debouncedLoadOptions}
					// The server already filtered; don't let react-select
					// re-filter loaded options against the typed text by label
					// (that would hide matches whose label lacks the term, e.g.
					// a pasted GUID or an email-only match).
					filterOption={null}
					value={selected}
					onChange={handleChange}
					placeholder={placeholder}
					isDisabled={isDisabled}
					isClearable
					{...(isMulti ? { isMulti: true } : {})}
					error={error}
					autoFocus={autoFocus}
				/>
				{error ? (
					<div
						className="invalid-feedback"
						style={{ display: "contents" }}
					>
						Mandatory field
					</div>
				) : null}
			</FormControlWrapper>
		</Form.Group>
	);
};
